import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Trans, withTranslation } from "react-i18next";

import UserBar from "../components/UserBar";
import Queries from "./videos/Queries";
import QueryResult from "./videos/QueryResult";
import Clips from "./videos/Clips";
import Tracks from "./videos/Tracks";
import Logos from "./videos/Logos";
import Overview from "./videos/Overview";
import Transcript from "./videos/Transcript";
import Scenes from "./videos/Scenes";
import Redaction from "./videos/Redaction";
import Search from "./videos/Search";
import axios from "../components/Axios";
import { withCdnCookie } from "../app/cdnCookie";

import { fetchSentences } from "../slices/sentences";
import { fetchCharacters } from "../slices/characters";
import { fmt as formatDuration } from "./videos/scenes/sceneModel";

// tabs built on the design tokens, which have a dark mode
const THEMED_TABS = ["overview", "tracks", "transcript", "scenes"];
// tabs whose header shows the scene stats (scenes, story arcs, characters, runtime)
const STATS_TABS = ["overview", "scenes"];
// tab names for the browser tab's title
const TAB_LABELS = { overview: "Overview", scenes: "Scenes", transcript: "Transcript", tracks: "Tracks", logos: "Logos", redaction: "Redaction", queries: "Queries", clips: "Clips" };

class Index extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      project: {
        project_name: "Video Title",
        project_id: null,
      },
      loading: false,
      playerSource: undefined,
      video: { video_name: "Source video" },
      loaded: {
        redaction: false,
        queries: false,
        search: false,
      },
      block_access: false,
      total_duration: 0,
      scene_stats: null,
    };
    this.navTo = this.navTo.bind(this);
    this.setSceneStats = (scene_stats) => this.setState({ scene_stats });

    // the video's time, shared by the tabs' players: another tab carries on where the video was. ?t= (seconds)
    // opens the tab at that time (links from search results)
    const t0 = Number(new URLSearchParams((props.location && props.location.search) || "").get("t"));
    this.playhead = t0 > 0 ? t0 : 0;
    this.onTime = (time) => {
      this.playhead = time;
    };
  }

  sceneSubtitle() {
    const s = this.state.scene_stats;
    if (!s || !s.scenes) return "";
    const { t } = this.props;
    const n = (count, one, many) => (count === 1 ? t(one) : t(many, { count }));
    const parts = [n(s.scenes, "1 scene", "{{count}} scenes")];
    if (s.arcs) parts.push(n(s.arcs, "1 story arc", "{{count}} story arcs"));
    if (s.characters) parts.push(n(s.characters, "1 character", "{{count}} characters"));
    // the video's own duration, as in the library; the last scene can end a second short of it
    const duration = this.state.project.duration > 0 ? this.state.project.duration : s.duration;
    parts.push(t("{{time}} runtime", { time: formatDuration(duration) }));
    return parts.join(" · ");
  }

  // the Summary tab is now the Overview tab: keep old links working
  redirectOldTab() {
    if (this.props.match.params.tab === "summary") this.props.history.replace("overview");
  }

  componentDidUpdate() {
    this.redirectOldTab();
  }

  componentDidMount() {
    this.redirectOldTab();

    //set loaded state
    this.setState(function (state) {
      state.loaded[this.props.match.params.tab] = true;
      return state;
    });

    //characters
    this.props.dispatch(fetchCharacters(this.props.match.params.project_id));
    this.props.dispatch(fetchSentences(this.props.match.params.project_id));

    //video details; the tabs (and their CDN media) render once the CDN cookie is set too
    withCdnCookie(axios.get("/video_project/" + this.props.match.params.project_id))
      .then(
        function (response) {
          this.setState({ loading: false, project: response.data });
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error);
          this.setState({ loading: false });
        }.bind(this)
      );

    //video
    withCdnCookie(
      axios.get(
        "/video_project/" +
        this.props.match.params.project_id +
        "/videos?video_type=LOW_RESOLUTION"
      )
    )
      .then(
        function (response) {
          if (0 in response.data) {
            response.data[0].video_name = "Source video";
            this.setState({
              playerSource: response.data[0].video_path,
              video: response.data[0],
            });
          }
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error);
        }.bind(this)
      );

    //data access
    axios.get("/user/data_access")
      .then(
        function (response) {
          if (response.data.access === false) {
            let d = response.data.total_duration / 3600
            this.setState({ block_access: true, total_duration: d.toFixed(1) });
          }
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error);
        }.bind(this)
      );
  }

  navTo(p, e) {
    e.preventDefault();
    //set loaded state
    this.setState(function (state) {
      state.loaded[p] = true;
      return state;
    });
    this.props.history.push(p);
  }

  render() {
    return (
      <>
        <UserBar
          wide
          themed={THEMED_TABS.includes(this.props.match.params.tab)}
          title={this.state.project.project_name}
          docTitle={
            this.state.project.project_id === null
              ? ""
              : this.state.project.project_name + " · " + this.props.t(TAB_LABELS[this.props.match.params.tab] || "Overview")
          }
          subtitle={STATS_TABS.includes(this.props.match.params.tab) ? this.sceneSubtitle() : ""}
          align="flex-end"
        >
          <Tabs
            value={this.props.match.params.tab || "overview"}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab
              label={<Trans>Overview</Trans>}
              value="overview"
              href="overview"
              onClick={this.navTo.bind(null, "overview")}
            />
            <Tab
              label={<Trans>Scenes</Trans>}
              value="scenes"
              href="scenes"
              onClick={this.navTo.bind(null, "scenes")}
            />
            <Tab
              label={<Trans>Transcript</Trans>}
              value="transcript"
              href="transcript"
              onClick={this.navTo.bind(null, "transcript")}
            />
            {/* last: fixing face tracks and names, rather than reading about the video */}
            <Tab
              label={<Trans>Tracks</Trans>}
              value="tracks"
              href="tracks"
              onClick={this.navTo.bind(null, "tracks")}
            />
            <Tab
              hidden
              label={<Trans>Logos</Trans>}
              value="logos"
              href="logos"
              onClick={this.navTo.bind(null, "logos")}
            />
            <Tab
              hidden
              label={<Trans>Redaction</Trans>}
              value="redaction"
              href="redaction"
              onClick={this.navTo.bind(null, "redaction")}
            />
            <Tab
              hidden
              label={<Trans>Queries</Trans>}
              value="queries"
              href="queries"
              onClick={this.navTo.bind(null, "queries")}
            />
            <Tab
              hidden
              label="Query Result"
              value="query_result"
              href="query_result"
              onClick={this.navTo.bind(null, "query_result")}
            />
            {/* still reached from Query Result's "View Clips" link */}
            <Tab
              hidden
              label={<Trans>Clips</Trans>}
              value="clips"
              href="clips"
              onClick={this.navTo.bind(null, "clips")}
            />
          </Tabs>
        </UserBar>
        {this.state.project.project_id !== null && (
          <>
            {this.props.match.params.tab === "overview" && (
              <Overview
                project={this.state.project}
                videosrc={this.state.playerSource}
                onStats={this.setSceneStats}
                startTime={this.playhead}
                onTime={this.onTime}
              />
            )}
            {this.props.match.params.tab === "tracks" && (
              <Tracks project={this.state.project} />
            )}
            {this.props.match.params.tab === "logos" && (
              <Logos
                project={this.state.project}
                videosrc={this.state.playerSource}
              />
            )}
            {this.props.match.params.tab === "transcript" && (
              <Transcript
                project={this.state.project}
                videosrc={this.state.playerSource}
                startTime={this.playhead}
                onTime={this.onTime}
              />
            )}
            {this.props.match.params.tab === "scenes" && (
              <Scenes
                project={this.state.project}
                videosrc={this.state.playerSource}
                onStats={this.setSceneStats}
                startTime={this.playhead}
                onTime={this.onTime}
              />
            )}
            {this.props.match.params.tab === "clips" && (
              <Clips project={this.state.project} />
            )}
            {this.props.match.params.tab === "query_result" && (
              <QueryResult project={this.state.project} />
            )}
            {this.state.loaded.queries && (
              <div hidden={this.props.match.params.tab !== "queries"}>
                <Queries
                  project={this.state.project}
                  videosrc={this.state.playerSource}
                />
              </div>
            )}
            {this.state.loaded.search && (
              <div hidden={this.props.match.params.tab !== "search"}>
                <Search
                  project={this.state.project}
                  videosrc={this.state.playerSource}
                />
              </div>
            )}
            {this.state.loaded.redaction && (
              <div hidden={this.props.match.params.tab !== "redaction"}>
                <Redaction
                  project={this.state.project}
                  video={this.state.video}
                  videosrc={this.state.playerSource}
                />
              </div>
            )}
          </>
        )}
        <Dialog open={this.state.block_access}>
          <DialogTitle>{"Subscription Required"}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Trans>Your</Trans> {this.state.total_duration}-
              <Trans>
                hour video usage is over the 1-hour free trial limit. To see
                this content, Subscribe or remove content.
              </Trans>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.props.history.goBack.bind(this)}
              color="primary"
            >
              <Trans>Go Back</Trans>
            </Button>
            <Button
              onClick={() => {
                this.props.history.push("/account/billing");
              }}
              variant="contained"
              color="primary"
              autoFocus
            >
              <Trans>Subscribe</Trans>
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

const mapState = (state) => {
  return {};
};
export default withTranslation()(withRouter(connect(mapState)(Index)));
